import { HttpClient } from '@angular/common/http';
import { inject, Service, signal } from '@angular/core';
import { Garage } from '@core/interfaces/garage.interface';
import { AuthService } from '../auth/auth.service';
import { LocalStorageService } from '../local-storage/local-storage.service';
import { environment } from '@environments/environment';
import { ApiResponse } from '@core/interfaces/api-response.interface';
import { Observable, map, tap } from 'rxjs';
import { LocalStorageKeys } from '@core/types/local-storage.type';

@Service()
export class GarageService {
  private readonly http = inject(HttpClient);
  private readonly localStorageService = inject(LocalStorageService);
  private readonly authService = inject(AuthService);

  public readonly baseUrl = environment.apiURL + '/garages';

  private readonly garagesState = signal<Array<Garage>>([]);
  private readonly selectedGarageState = signal<Garage | undefined>(undefined);

  public readonly garages = this.garagesState.asReadonly();
  public readonly selectedGarage = this.selectedGarageState.asReadonly();

  /** Restores user-scoped state to its initial values. */
  public reset(): void {
    this.garagesState.set([]);
    this.selectedGarageState.set(undefined);
  }

  /* Loads the current user's garage and restores the active selection. */
  public loadGarages(): Observable<Array<Garage>> {
    return this.http.get<ApiResponse<Array<Garage>>>(`${environment.apiURL}/user/garages`).pipe(
      map((res) => res.data!),
      tap((res) => {
        this.garagesState.set(res);
        this.restoreSelectedGarage();
      })
    );
  }

  /* Selects a garage and persists the selection. */
  private restoreSelectedGarage(): void {
    const garages = this.garagesState();
    if (garages.length === 0) {
      this.selectedGarageState.set(undefined);
      this.persistSelectedGarageId(undefined);
      return;
    }

    const storedGarageId = this.getPersistedSelectedGarageId();
    const selectedGarage = garages.find((garage) => garage.id === storedGarageId) ?? garages[0];

    this.selectedGarageState.set(selectedGarage);
    this.persistSelectedGarageId(selectedGarage.id);
  }

  /* Reads the persisted selected garage ID scoped by authenticated user ID. */
  private getPersistedSelectedGarageId(): string | undefined {
    const userId = this.authService.user()?.id;
    if (!userId) {
      return undefined;
    }

    const selectedByUser = this.localStorageService.get<Record<string, string>>(LocalStorageKeys.SelectedGarageByUser);
    return selectedByUser?.[userId];
  }

  /* Persists or clears the selected garage ID for the authenticated user. */
  private persistSelectedGarageId(garageId: string | undefined): void {
    const userId = this.authService.user()?.id;
    if (!userId) {
      return;
    }

    const selectedByUser =
      this.localStorageService.get<Record<string, string>>(LocalStorageKeys.SelectedGarageByUser) ?? {};

    if (garageId) {
      selectedByUser[userId] = garageId;
    } else {
      delete selectedByUser[userId];
    }

    this.localStorageService.set(LocalStorageKeys.SelectedGarageByUser, selectedByUser);
  }
}
